import { useEffect } from "react";

const DEFAULT_LICENSE = "https://barbequeen.com/terms-and-conditions";
const DEFAULT_ACQUIRE = "https://barbequeen.com/contact-us";
const DEFAULT_COPYRIGHT =
  "© 2026 Barbequeen - Nonvee Foods Private Limited. All rights reserved.";
const DEFAULT_CREDIT = "Barbequeen Restaurant";

function enrichSchema(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) {
    return data.map(enrichSchema);
  }
  const enriched = { ...data };
  if (enriched["@type"] === "ImageObject") {
    if (!enriched.license) enriched.license = DEFAULT_LICENSE;
    if (!enriched.acquireLicensePage)
      enriched.acquireLicensePage = DEFAULT_ACQUIRE;
    if (!enriched.copyrightNotice) enriched.copyrightNotice = DEFAULT_COPYRIGHT;
    if (!enriched.creditText) enriched.creditText = DEFAULT_CREDIT;
    if (!enriched.contentUrl && enriched.url)
      enriched.contentUrl = enriched.url;
  }
  for (const key of Object.keys(enriched)) {
    if (enriched[key] && typeof enriched[key] === "object") {
      enriched[key] = enrichSchema(enriched[key]);
    }
  }
  return enriched;
}

const SEO = ({
  title,
  metaTitle,
  description,
  keywords,
  robots,
  author,
  canonical,
  og,
  geo,
  twitter,
  schema,
}) => {
  useEffect(() => {
    // 1. Title
    if (title) {
      document.title = title;
    }

    // Helper to set or create <meta>
    const setMeta = (attrName, attrValue, content) => {
      if (content === undefined || content === null || content === "") return;
      let element = document.head.querySelector(
        `meta[${attrName}="${attrValue}"]`,
      );
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper to set or create <link>
    const setLink = (rel, href) => {
      if (!href) return;
      let element = document.head.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Standard Meta Tags
    setMeta("name", "title", metaTitle || title || "");
    setMeta("name", "description", description || "");
    setMeta("name", "keywords", keywords || "");
    setMeta("name", "robots", robots || "index, follow");
    if (author) {
      setMeta("name", "author", author);
    }

    // Canonical
    if (canonical) {
      setLink("canonical", canonical);
    }

    // Geo Meta Tags
    if (geo) {
      if (geo.region) setMeta("name", "geo.region", geo.region);
      if (geo.placename) setMeta("name", "geo.placename", geo.placename);
      if (geo.position) {
        setMeta("name", "geo.position", geo.position);
        setMeta("name", "ICBM", geo.icbm || geo.position.replace(";", ", "));
      }
      if (geo.icbm) setMeta("name", "ICBM", geo.icbm);
    }

    // Open Graph (OG) Tags
    if (og) {
      if (og.type) setMeta("property", "og:type", og.type);
      if (og.url || canonical)
        setMeta("property", "og:url", og.url || canonical);
      if (og.title || title) setMeta("property", "og:title", og.title || title);
      if (og.description || description)
        setMeta("property", "og:description", og.description || description);
      if (og.image) setMeta("property", "og:image", og.image);
      if (og.imageWidth)
        setMeta("property", "og:image:width", String(og.imageWidth));
      if (og.imageHeight)
        setMeta("property", "og:image:height", String(og.imageHeight));
      if (og.siteName)
        setMeta(
          "property",
          "og:site_name",
          og.siteName || "Barbequeen Restaurant",
        );
      if (og.locale) setMeta("property", "og:locale", og.locale || "en_IN");
    }

    // Twitter Card Tags
    const twCard = twitter?.card || "summary_large_image";
    const twTitle = twitter?.title || og?.title || title;
    const twDesc = twitter?.description || og?.description || description;
    const twImage = twitter?.image || og?.image;
    const twSite = twitter?.site || "@barbequeen";

    setMeta("name", "twitter:card", twCard);
    if (twSite) setMeta("name", "twitter:site", twSite);
    if (twTitle) setMeta("name", "twitter:title", twTitle);
    if (twDesc) setMeta("name", "twitter:description", twDesc);
    if (twImage) setMeta("name", "twitter:image", twImage);

    // Dynamic Schema Management in <head>
    // Remove old dynamic schema scripts
    const oldSchemas = document.querySelectorAll(
      'script[data-react-seo-schema="true"], script[type="application/ld+json"][id^="seo-jsonld"]',
    );
    oldSchemas.forEach((el) => el.remove());

    // Inject current schema(s)
    if (schema) {
      const schemasToInject = Array.isArray(schema) ? schema : [schema];
      const processedList = [];

      schemasToInject.forEach((s, idx) => {
        if (!s) return;
        const processed = enrichSchema(s);
        processedList.push(processed);

        const scriptEl = document.createElement("script");
        scriptEl.type = "application/ld+json";
        scriptEl.setAttribute("data-react-seo-schema", "true");
        scriptEl.id = `seo-jsonld-schema-${idx + 1}`;
        scriptEl.textContent = JSON.stringify(processed, null, 2);
        document.head.appendChild(scriptEl);
      });

      if (typeof window !== "undefined") {
        window.__SCHEMA__ = processedList;
        window.__SEO__ = {
          title,
          description,
          keywords,
          canonical,
          geo,
          og,
          schema: processedList,
        };
      }
    }

    return () => {
      // Cleanup dynamically injected scripts when component unmounts
      const dynamicScripts = document.querySelectorAll(
        'script[data-react-seo-schema="true"], script[type="application/ld+json"][id^="seo-jsonld"]',
      );
      dynamicScripts.forEach((el) => el.remove());
    };
  }, [
    title,
    metaTitle,
    description,
    keywords,
    robots,
    author,
    canonical,
    og,
    geo,
    twitter,
    schema,
  ]);

  return null;
};

export default SEO;
